import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiFileText, FiActivity, FiClock, FiLayers } from 'react-icons/fi';
import { statsAPI, channelAPI } from '../services/api';
import { useWebSocket } from '../context/WebSocketContext';
import StatsCard from '../components/StatsCard';
import RealtimeBadge from '../components/RealtimeBadge';
import { fmtTime, fmtDateTime } from '../utils/time';
import { toast } from 'react-toastify';
import '../styles/Dashboard.css';

const Dashboard = () => {
    const [stats, setStats] = useState(null);
    const [channelGroups, setChannelGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedGroup, setSelectedGroup] = useState('');
    const [moneyRange, setMoneyRange] = useState('7d');   // today | 24h | 7d | 30d | all
    const { realtimeLogs, newLogCount, clearNewLogCount, connected } = useWebSocket();

    useEffect(() => {
        fetchDashboardData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [moneyRange]);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            // Stats là chính — phải load. Channels là legacy, có thể fail mà không crash.
            const overviewRes = await statsAPI.getOverview({ moneyRange });
            setStats(overviewRes.data);
            try {
                const groupsRes = await channelAPI.getGroups();
                setChannelGroups(groupsRes.data);
            } catch { /* legacy route, ignore */ }
        } catch (error) {
            toast.error('Không thể tải dữ liệu dashboard');
        } finally {
            setLoading(false);
        }
    };

    const getActionTypeColor = (actionType) => {
        const colors = {
            'item_pickup':         '#34d399',
            'item_drop':           '#f87171',
            'money_transaction':   '#fbbf24',
            'vehicle_spawn':       '#60a5fa',
            'player_connect':      '#10b981',
            'player_disconnect':   '#ef4444',
            'admin_action':        '#8b5cf6',
            'anticheat_detection': '#f59e0b',
            'chat_message':        '#06b6d4',
            'weapon_fire':         '#dc2626',
            'other':               '#6b7280'
        };
        return colors[actionType] || colors.other;
    };

    const getActionTypeLabel = (actionType) => {
        const labels = {
            'item_pickup':         'Item+',
            'item_drop':           'Item-',
            'money_transaction':   'Tiền',
            'vehicle_spawn':       'Xe',
            'player_connect':      'Join',
            'player_disconnect':   'Leave',
            'admin_action':        'Admin',
            'anticheat_detection': '⚠ Cheat',
            'chat_message':        'Chat',
            'weapon_fire':         'Vũ khí',
            'other':               'Khác'
        };
        return labels[actionType] || 'Khác';
    };

    // Ưu tiên playerSteamId, nếu null thì fallback về details
    const getDisplayId = (log) => {
        if (log.playerSteamId) return log.playerSteamId;
        if (log.details?.serverId) return `ID:${log.details.serverId}`;
        if (log.details?.citizenId) return `CID:${log.details.citizenId}`;
        if (log.details?.playerCode) return log.details.playerCode;
        return null;
    };

    // Trả về tên người chơi sạch, fallback từ action string nếu parse sai
    const getPlayerDisplay = (log) => {
        const BAD = /^(nhân vật|tên|thêm vật|xóa vật|mua|vật phẩm)/i;
        let name = log.playerName;
        if (!name || BAD.test(name.trim())) {
            // Thử extract từ action: "XXX | PlayerName (ID:xxx)"
            const m = (log.action || '').match(/\|\s*([^|()\n]{2,}?)\s*\(ID:\d+\)/i);
            if (m) name = m[1].trim();
        }
        const id = getDisplayId(log);
        if (name && id) return `${name} (${id})`;
        if (name)       return name;
        if (id)         return id;
        return '-';
    };

    // Group spam-volume: ẩn khỏi Live Feed mặc định (vẫn xem ở tab dedicated hoặc chọn từ dropdown).
    // Damage/Death xem ở Tab "Sát Thương"/"Tử Vong"; Hệ Thống xem ở "Log Người Chơi".
    const DASHBOARD_EXCLUDE_GROUPS = new Set([
        'DAMAGE',         // 🩸 Damage Log + 🔫 Shooting Log
        'DEATHS',         // 💀 Death Log
        'SYSTEM_LOGS',    // 📋 All Logs + 📥 Join + 📤 Leave + 💬 Chat (high volume duplicates)
        'EXPLOSIONS_AC',  // 🧨 Explosion Log
    ]);

    const filteredLogs = realtimeLogs.filter(log => {
        const displayId = getDisplayId(log);
        const matchesSearch = !searchTerm ||
            (log.playerName && log.playerName.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (displayId && displayId.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (log.action && log.action.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (log.rawContent && log.rawContent.toLowerCase().includes(searchTerm.toLowerCase()));

        // Khi không filter group cụ thể → ẩn các group ồn ào
        const matchesGroup = selectedGroup
            ? log.channelGroup === selectedGroup
            : !DASHBOARD_EXCLUDE_GROUPS.has(log.channelGroup);

        return matchesSearch && matchesGroup;
    });

    const cashNet = (stats?.moneyStats?.cash?.added || 0) - (stats?.moneyStats?.cash?.removed || 0);
    const bankNet = (stats?.moneyStats?.bank?.added || 0) - (stats?.moneyStats?.bank?.removed || 0);

    if (loading) {
        return (
            <div className="dashboard-loading">
                <div className="loading-spinner"></div>
                <p>Đang tải dashboard...</p>
            </div>
        );
    }

    return (
        <div className="dashboard">
            <div className="dashboard-header">
                <div>
                    <h1>Dashboard</h1>
                    <p className="dashboard-subtitle">RVN City Log Overview</p>
                </div>
                <RealtimeBadge />
            </div>

            <div className="stats-grid">
                <StatsCard title="Tổng Logs"  value={stats?.totals?.all}   icon={<FiFileText />} color="#6366f1" subtitle="Tất cả thời gian" />
                <StatsCard title="Hôm Nay"    value={stats?.totals?.today} icon={<FiClock />}    color="#34d399" subtitle="24 giờ qua" />
                <StatsCard title="Tuần Này"   value={stats?.totals?.week}  icon={<FiActivity />} color="#60a5fa" subtitle="7 ngày qua" />
                <StatsCard title="Tháng Này"  value={stats?.totals?.month} icon={<FiLayers />}   color="#a78bfa" subtitle="30 ngày qua" />
            </div>

            {stats?.moneyStats && (
                <>
                    <div className="money-range-bar">
                        <span className="money-range-label">💰 Dòng tiền theo:</span>
                        {[
                            { k: 'today', label: 'Hôm nay' },
                            { k: '24h',   label: '24h qua' },
                            { k: '7d',    label: '7 ngày' },
                            { k: '30d',   label: '30 ngày' },
                            { k: 'all',   label: 'Tất cả' },
                        ].map(o => (
                            <button
                                key={o.k}
                                className={`money-range-btn ${moneyRange === o.k ? 'active' : ''}`}
                                onClick={() => setMoneyRange(o.k)}
                            >
                                {o.label}
                            </button>
                        ))}
                        {stats.moneySince && (
                            <span className="money-range-since">
                                từ {fmtDateTime(stats.moneySince)}
                            </span>
                        )}
                    </div>
                    <div className="money-row">
                        <div className="money-row-card cash">
                            <span className="money-icon">💵</span>
                            <span className="money-label">Cash</span>
                            <span className="money-add" title="Tiền cash mọi người chơi nhận được">
                                <span className="money-kpi-label">Tiền Kiếm</span>
                                +${(stats.moneyStats.cash?.added || 0).toLocaleString()}
                            </span>
                            <span className="money-sep">|</span>
                            <span className="money-sub" title="Tiền cash mọi người chơi chi tiêu">
                                <span className="money-kpi-label">Tiền Chi</span>
                                -${(stats.moneyStats.cash?.removed || 0).toLocaleString()}
                            </span>
                            <span className="money-sep">|</span>
                            <span className={`money-net ${cashNet >= 0 ? 'pos' : 'neg'}`} title="Chênh lệch = Kiếm - Chi (dương → thành phố đang phình tiền)">
                                <span className="money-kpi-label">Tồn Đọng</span>
                                {cashNet >= 0 ? '+' : ''}{cashNet.toLocaleString()}$
                            </span>
                        </div>
                        <div className="money-row-card bank">
                            <span className="money-icon">🏦</span>
                            <span className="money-label">Bank</span>
                            <span className="money-add" title="Tiền bank mọi người chơi nhận được">
                                <span className="money-kpi-label">Tiền Kiếm</span>
                                +${(stats.moneyStats.bank?.added || 0).toLocaleString()}
                            </span>
                            <span className="money-sep">|</span>
                            <span className="money-sub" title="Tiền bank mọi người chơi chi tiêu">
                                <span className="money-kpi-label">Tiền Chi</span>
                                -${(stats.moneyStats.bank?.removed || 0).toLocaleString()}
                            </span>
                            <span className="money-sep">|</span>
                            <span className={`money-net ${bankNet >= 0 ? 'pos' : 'neg'}`} title="Chênh lệch = Kiếm - Chi">
                                <span className="money-kpi-label">Tồn Đọng</span>
                                {bankNet >= 0 ? '+' : ''}{bankNet.toLocaleString()}$
                            </span>
                        </div>
                    </div>
                </>
            )}

            <div className="livefeed-section">
                <div className="livefeed-header">
                    <div className="livefeed-title">
                        <span className="live-dot"></span>
                        Live Feed Realtime
                        {newLogCount > 0 && (
                            <span className="new-badge" onClick={clearNewLogCount}>
                                +{newLogCount} mới
                            </span>
                        )}
                    </div>
                    <div className="livefeed-controls">
                        <input
                            type="text"
                            placeholder="Tìm tên, ID, nội dung..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="livefeed-search"
                        />
                        <select
                            value={selectedGroup}
                            onChange={(e) => setSelectedGroup(e.target.value)}
                            className="livefeed-filter"
                        >
                            <option value="">Tất cả nhóm</option>
                            {channelGroups.map(group => (
                                <option key={group.key} value={group.key}>{group.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="lf-table-wrap">
                    <div className="lf-thead">
                        <span>Giờ</span>
                        <span>Nhóm</span>
                        <span>Kênh</span>
                        <span>Người Chơi (ID)</span>
                        <span>Nội Dung</span>
                        <span>Loại</span>
                    </div>
                    <div className="lf-tbody">
                        {filteredLogs.length === 0 ? (
                            <div className="lf-empty">
                                {connected ? '⏳ Đang chờ logs mới...' : '🔌 Đang kết nối WebSocket...'}
                            </div>
                        ) : (
                            filteredLogs.slice(0, 100).map((log, i) => {
                                const color = getActionTypeColor(log.actionType);
                                return (
                                    <div key={log._id || i} className="lf-row">
                                        <span className="lf-time">
                                            {log.timestamp ? fmtTime(log.timestamp) : '--:--:--'}
                                        </span>
                                        <span className="lf-group" style={{ color: log.channelGroupColor || '#f0b429' }}>
                                            {log.channelGroupLabel || log.channelGroup || '-'}
                                        </span>
                                        <span className="lf-channel">
                                            #{log.channelLabel || log.channelKey || '-'}
                                        </span>
                                        <span className="lf-player" title={getPlayerDisplay(log)}>
                                            {getPlayerDisplay(log)}
                                        </span>
                                        <span className="lf-action" title={log.action || log.rawContent}>
                                            {log.action || log.rawContent?.substring(0, 120)}
                                        </span>
                                        <span className="lf-type" style={{ backgroundColor: color }}>
                                            {getActionTypeLabel(log.actionType)}
                                        </span>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                <div className="livefeed-footer">
                    <span>Hiển thị {Math.min(filteredLogs.length, 100)} / {realtimeLogs.length} logs</span>
                    <Link to="/logs" className="view-all-link">Xem tất cả logs →</Link>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
